// src/belediye/components/utils/FilterSheet.js
//
// PAYLAŞILAN filtre çekmecesi — tüm sayfalarda AYNI davranış:
//   • Masaüstü + TABLET: butona çıpalı açılır panel (popover)
//   • Telefon:          ALTTAN açılan çekmece (bottom-sheet) + karartma
//
// Telefon sheet'i React Portal ile document.body'ye render edilir → sticky/sidebar
// gibi stacking-context'lerin ALTINDA kalmaz, her zaman en üstte gelir.
//
// Kullanım:
//   <FilterSheet activeCount={n} onClear={fn} title="Filtrele">
//     {/* filtre alanları (children) */}
//   </FilterSheet>

import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  AdjustmentsHorizontalIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import useKatman from "../../hooks/useKatman";
import "./FilterSheet.css";

/* FAZ-99 / Y-4 — ÇEKMECE YALNIZ TELEFONDA.

   Kullanıcı (2026-08-02): "[tablette] filtrede olmasına rağmen aşağıdan
   çekmece değil de web'teki filtreler kısmı gibi açılsın."

   Eski eşik çıplak `max-width: 768px` idi ve iPad mini DİKEY tam 768px
   raporluyor: aynı iPad'in dikeyinde alttan çekmece, iPad Air'de (820) çıpalı
   popover açılıyordu. FAZ-98'de navbar ve sekmelerde yakalanan 768 tuzağının
   üçüncü örneği. Artık katman sözleşmesinden okunuyor (olcu.js). */
const useIsMobile = () => useKatman() === "telefon";

export default function FilterSheet({
  activeCount = 0,
  onClear,
  title = "Filtrele",
  label = "Filtrele",
  children,
}) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();

  const wrapRef = useRef(null);
  const panelRef = useRef(null);

  // Masaüstü: dışarı tıkla / Escape ile kapan (mobilde backdrop kapatır)
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && wrapRef.current.contains(e.target)) return;
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Mobil sheet açıkken arka planı kilitle
  useEffect(() => {
    if (!(open && isMobile)) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open, isMobile]);

  const panelInner = (
    <>
      <div className="fs-head">
        <span className="fs-title">{title}</span>
        <div className="fs-head-actions">
          {activeCount > 0 && typeof onClear === "function" ? (
            <button type="button" className="fs-clear" onClick={onClear}>
              Temizle
            </button>
          ) : null}
          <button
            type="button"
            className="fs-close"
            onClick={() => setOpen(false)}
            aria-label="Kapat"
          >
            <XMarkIcon />
          </button>
        </div>
      </div>
      <div className="fs-body">{children}</div>
    </>
  );

  return (
    <div className="fs-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`fs-trigger${activeCount > 0 ? " is-active" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        title={title}
      >
        <AdjustmentsHorizontalIcon />
        <span>{label}</span>
        {activeCount > 0 ? <span className="fs-badge">{activeCount}</span> : null}
      </button>

      {/* Masaüstü: çıpalı popover (wrap içinde) */}
      {open && !isMobile ? (
        <div className="fs-popover" role="dialog" ref={panelRef}>
          {panelInner}
        </div>
      ) : null}

      {/* Mobil: portal ile alttan çekmece + backdrop (her şeyin üstünde) */}
      {open && isMobile
        ? createPortal(
            <div className="fs-overlay">
              <div
                className="fs-backdrop"
                onClick={() => setOpen(false)}
                aria-hidden="true"
              />
              <div className="fs-sheet" role="dialog" ref={panelRef}>
                <div className="fs-grabber" aria-hidden="true" />
                {panelInner}
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
